import { cn } from '@gymcoach/ui';
import type { ReactNode } from 'react';

import { monogramFor } from '@/shell/GymMark';

import { authCopy } from './copy';

/**
 * The frame of every signed-out (and not-yet-in) screen: sign-in, reset, forced password
 * change (DESIGN.md A1/A2). Desktop: a `hero` panel on the left 45% with the gym's mark
 * and one line; the form card on the right. Phone: the hero is a band on top.
 */
export function AuthFrame({
  gymName,
  logoUrl,
  children,
  footer,
  pitch,
}: {
  /** The gym, once known (forced change); Fitnatic on the sign-in page. */
  gymName?: string | null;
  logoUrl?: string | null;
  children: ReactNode;
  footer?: ReactNode;
  /** In place of the sign-in words on the brand side (the free-trial page). */
  pitch?: { line: string; detail: string; points?: readonly string[]; note?: string };
}) {
  const name = gymName ?? 'Fitnatic';
  // Before a gym is known the mark is Fitnatic's own icon (web/public/fitnatic-icon.svg).
  const mark = logoUrl ?? (gymName ? null : '/fitnatic-icon.svg');
  return (
    <div className="flex min-h-screen flex-col bg-canvas lg:flex-row">
      <aside
        className={cn(
          'relative overflow-hidden bg-hero text-hero-ink',
          'rounded-b-[28px] px-6 pb-8 pt-10 lg:w-[45%] lg:rounded-none lg:px-14 lg:py-14',
          'flex flex-col lg:justify-between',
        )}
      >
        {/* The brand glow: a soft radial of the gym's colour at the top right. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(circle at 90% 0%, hsl(var(--primary) / 0.28), transparent 55%)' }}
        />
        <div className="relative flex items-center gap-3">
          {mark ? (
            <img src={mark} alt="" aria-hidden="true" className="h-12 w-12 rounded-[12px] object-cover" />
          ) : (
            <span
              aria-hidden="true"
              className="flex h-12 w-12 items-center justify-center rounded-[12px] bg-primary text-lg font-extrabold tracking-wide text-primary-ink"
            >
              {monogramFor(name)}
            </span>
          )}
          <span className="flex flex-col leading-tight">
            <span className="text-lg font-semibold">{name}</span>
            {!gymName && <span className="text-sm text-hero-muted">{authCopy.byline}</span>}
          </span>
        </div>
        <div className="relative mt-6 lg:mt-0">
          <p className="text-2xl font-bold leading-tight tracking-tight lg:text-[2.5rem] lg:leading-[1.1]">
            {pitch?.line ?? authCopy.brandLine}
          </p>
          <p className="mt-2 max-w-sm text-base text-hero-muted lg:mt-4 lg:text-lg">{pitch?.detail ?? authCopy.brandDetail}</p>
          {pitch?.points && (
            <ul className="mt-5 hidden space-y-2.5 text-base sm:block lg:mt-8">
              {pitch.points.map((point) => (
                <li key={point} className="flex gap-2.5">
                  <svg aria-hidden="true" viewBox="0 0 20 20" className="mt-0.5 h-5 w-5 shrink-0 text-primary" fill="currentColor">
                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.6l7.3-7.3a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                  </svg>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="relative hidden text-sm text-hero-muted lg:block">{pitch?.note ?? authCopy.members}</p>
      </aside>

      <main className="flex flex-1 justify-center px-4 py-8 lg:items-center lg:py-12">
        <div className="w-full max-w-[400px]">
          <div className="rounded-lg border bg-surface p-6 shadow-sm sm:p-7">{children}</div>
          {footer && <div className="mt-4 text-center text-sm text-ink-muted">{footer}</div>}
          <p className="mt-4 text-center text-xs text-ink-subtle lg:hidden">{pitch?.note ?? authCopy.members}</p>
        </div>
      </main>
    </div>
  );
}
